import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Shield,
} from "lucide-react";
import { authAPI } from "@/api/auth";

interface ForgotPasswordProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPassword({ isOpen, onClose }: ForgotPasswordProps) {
  const [emailid, setEmailid] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateEmail = (emailid: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailid);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailid.trim()) {
      setError("Email address is required");
      return;
    }

    if (!validateEmail(emailid)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await authAPI.forgotPassword({ emailid: emailid.trim() });
      
      if (response.code === "200") {
        setIsSubmitting(false);
        setIsComplete(true);
        
        toast.success("Password reset email sent successfully!");
        
        setTimeout(() => {
          handleClose();
        }, 5000);
      } else {
        throw new Error(response.message || 'Failed to send password reset email');
      }
      
    } catch (error) {
      setIsSubmitting(false);
      setError(error instanceof Error ? error.message : 'Failed to send password reset email. Please try again.');
      
      toast.error(error instanceof Error ? error.message : 'Failed to send password reset email. Please try again.');
    }
  };

  const handleClose = () => {
    setEmailid("");
    setError(null);
    setIsComplete(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-primary" />
            <span>Reset Password</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
        
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {isComplete ? (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-muted-foreground mb-4">
                We've sent a password reset link to <strong>{emailid}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Mail className="w-4 h-4 text-primary mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm text-primary font-medium">Next Steps:</p>
                    <ul className="text-sm text-primary mt-1 space-y-1">
                      <li>• Check your email inbox</li>
                      <li>• Look for an email from ORbiT8</li>
                      <li>• Click the reset link in the email</li>
                      <li>• Create your new password</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Didn't receive an email? Check your spam folder or try again in a few minutes.
              </p>
            </div>
          ) : isSubmitting ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sending Reset Email...</h3>
              <p className="text-muted-foreground">Please wait while we send your password reset link.</p>
            </div>
          ) : (
            <div className="space-y-4">
             
              <div className="text-center">
                <p className="text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

            
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <Input
                      id="resetEmail"
                      type="email"
                      placeholder="Enter your email address"
                      value={emailid}
                      onChange={(e) => {
                        setEmailid(e.target.value);
                        if (error) setError(null); 
                      }}
                      className="pl-10"
                      maxLength={320}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !emailid.trim()}
                  className="w-full shadow-lg shadow-primary/20"
                >
                  {isSubmitting ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>

            
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
